<script setup lang="ts">
import type { NuxtError } from '#app'

const props = defineProps<{
  error: NuxtError
}>()

const isNotFound = computed(() => props.error?.statusCode === 404)

// Preload the two display fonts the 404 pattern leans on. error.vue is
// rendered outside NuxtLayout, so we prefer not to rely on cascaded
// async font loading; the preload hint ensures the display type is
// ready before first paint.
useHead({
  link: [ { crossorigin: '', href: '/_fonts/', rel: 'preconnect' } ],
  title: () => (isNotFound.value ? '404' : 'Something broke'),
})

function handleClearError() {
  clearError({ redirect: '/games/' })
}
</script>

<template>
  <div class="min-h-screen bg-brand-red text-brand-chalk relative flex flex-col items-center justify-center px-6 py-20 text-center overflow-hidden">
    <div class="absolute top-10 left-6 sm:top-16 sm:left-16">
      <SfgStar
        :size="80"
        :tilt="4"
        fill="yellow"
      />
    </div>
    <div class="absolute top-20 right-8 sm:top-24 sm:right-20">
      <SfgStar
        :size="60"
        :tilt="1"
        fill="chalk"
      />
    </div>

    <div
      v-if="isNotFound"
      class="font-display-alt leading-[0.95] text-brand-yellow"
      style="font-size: clamp(120px, 20vw, 200px)"
    >
      404
    </div>
    <div
      v-else
      class="font-display-alt leading-[0.95] text-brand-yellow"
      style="font-size: clamp(96px, 16vw, 160px)"
    >
      {{ error?.statusCode || 500 }}
    </div>

    <h1 class="font-display uppercase tracking-[0.08em] mt-6 text-[22px] sm:text-sticker-md">
      {{ isNotFound ? 'This page rolled off the table.' : 'Something broke on our side.' }}
    </h1>

    <p class="font-body text-body mt-4 max-w-xl">
      <template v-if="isNotFound">
        The link you followed doesn't exist (anymore). Head back to the library, or start a game.
      </template>
      <template v-else>
        Try refreshing. If it happens again, the contact form is the fastest way to tell us.
      </template>
    </p>

    <div class="mt-10">
      <SfgButton
        variant="primary"
        size="md"
        @click="handleClearError"
      >
        Back to the library
      </SfgButton>
    </div>
  </div>
</template>
